import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { downloadReportFile } from "../../../services/SavedReportService";

const EXTENSION_BY_TYPE = {
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/pdf": "pdf",
  "text/csv": "csv",
};

// The API sends the file name in Content-Disposition. Cross-origin, the
// browser only shows that header when the API exposes it; otherwise the name
// comes from the content type.
export const fileNameFromHeaders = (headers = {}) => {
  const disposition = headers["content-disposition"] || "";
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (encoded) {
    return decodeURIComponent(encoded[1].trim());
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  if (plain) {
    return plain[1].trim();
  }
  const type = (headers["content-type"] || "").split(";")[0].trim();
  return `report.${EXTENSION_BY_TYPE[type] || "xlsx"}`;
};

const ReportDownload = () => {
  const { logId } = useParams();
  const intl = useIntl();
  // { status: "loading" | "ready" | "unavailable", file, fileName }
  const [download, setDownload] = useState({ status: "loading" });

  useEffect(() => {
    let isCurrent = true;
    setDownload({ status: "loading" });
    downloadReportFile(logId)
      .then((response) => {
        if (isCurrent) {
          setDownload({
            status: "ready",
            file: new Blob([response.data], {
              type: response.headers?.["content-type"],
            }),
            fileName: fileNameFromHeaders(response.headers || {}),
          });
        }
      })
      .catch((error) => {
        console.error("Report download failed", error);
        if (isCurrent) {
          setDownload({ status: "unavailable" });
        }
      });
    return () => {
      isCurrent = false;
    };
  }, [logId]);

  // Same as the report export in ReportSaga: an object URL clicked through a link.
  const saveFile = () => {
    const url = window.URL.createObjectURL(download.file);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", download.fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="edexlms-page-content">
      <div className="edexlms-page-header">
        <div className="edexlms-page-title">
          {intl.formatMessage({ id: "saved_report_download_title" })}
        </div>
      </div>
      <div className="edexlms-page-body flex flex-column align-items-start gap-3">
        {download.status === "loading" && (
          <div className="flex align-items-center gap-3">
            <ProgressSpinner style={{ width: "2rem", height: "2rem" }} />
            <span>
              {intl.formatMessage({ id: "saved_report_download_loading" })}
            </span>
          </div>
        )}
        {download.status === "ready" && (
          <>
            <p>{intl.formatMessage({ id: "saved_report_download_ready" })}</p>
            <div className="flex align-items-center gap-3">
              <span className="pi pi-file" />
              <span>{download.fileName}</span>
              <Button
                type="button"
                icon="pi pi-download"
                label={intl.formatMessage({ id: "saved_report_download" })}
                onClick={saveFile}
              />
            </div>
          </>
        )}
        {download.status === "unavailable" && (
          <p>
            {intl.formatMessage({ id: "saved_report_download_unavailable" })}
          </p>
        )}
      </div>
    </div>
  );
};

export default ReportDownload;
